import {
  AfterViewInit,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  viewChild,
  Signal,
  ChangeDetectionStrategy,
  signal,
  WritableSignal,
  computed,
  input,
  InputSignal,
  OnInit,
} from '@angular/core';
import {
  ModalController,
  IonButton,
  IonIcon,
  IonSpinner,
} from '@ionic/angular/standalone';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AlfredClientConfig } from '../../alfred-client.config';
import { Image } from '../../models/questions/image.model';
import { fromResize } from '@lyceo/shared/util-resize-observable';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { addIcons } from 'ionicons';
import { close } from 'ionicons/icons';
import { PinchZoomComponent } from '../pinch-zoom/pinch-zoom.component';

/**
 * This component is used as a modal to compare 2 or more images.
 * On large screens they are shown horizontally. On small screens they
 * are shown vertically.
 *
 * The user can remove images that are shown when more than 2 images are
 * currently visible. Also the user can choose to show all images again
 * when there are a total of 2 left.
 *
 * Images can be zoomed in by the user to look at them in more detail.
 *
 * @example
 * this.modalController.create({
 *   component: CompareImagesComponent,
 *   componentProps: { images: images },
 *   cssClass: 'lib-alfred-client-modal-compare-images',
 * }).then(modal => modal.present());
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'lib-compare-images',
  templateUrl: './compare-images.component.html',
  styleUrls: ['./compare-images.component.scss'],
  imports: [IonButton, IonIcon, IonSpinner, PinchZoomComponent],
})
export class CompareImagesComponent implements AfterViewInit, OnInit {
  private readonly config: AlfredClientConfig = inject(AlfredClientConfig);
  private readonly modalController: ModalController = inject(ModalController);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  /**
   * Set all the images that should be shown to compare.
   */
  public readonly images: InputSignal<Image[]> = input.required({
    transform: (value) => value ?? [],
  });

  /**
   * All the image URL's.
   */
  protected readonly allImageUrls: Signal<string[]> = computed(() => {
    return this.images().map((image: Image) => {
      const url: string = image.url;

      return url.match(/^http[s]?:\/\//) ? url : this.config.alfredApiUrl + url;
    });
  });

  /**
   * Only the image URL's that are currently shown to the user.
   */
  protected readonly displayedImageUrls: WritableSignal<string[]> = signal<
    string[]
  >([]);

  /**
   * The container holding the images.
   * We use this to check whether the container is large or small.
   * {@see _isLargeContainer}
   */
  public readonly imageViewContainer: Signal<ElementRef> =
    viewChild.required<ElementRef>('imageViewContainer');

  /**
   * If the container is large.
   * This is set by checking the {@see imageViewContainer}.
   */
  public readonly isLargeContainer: WritableSignal<boolean | undefined> =
    signal(undefined);

  constructor() {
    addIcons({ close });
  }

  public ngOnInit(): void {
    this.showAll();
  }

  /**
   * @inheritDoc
   */
  public ngAfterViewInit(): void {
    fromResize(this.imageViewContainer().nativeElement)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((resize: ResizeObserverEntry) => resize.contentRect.width > 767),
        distinctUntilChanged(),
      )
      .subscribe((isLargeContainer: boolean) => {
        this.isLargeContainer.set(isLargeContainer);
      });
  }

  /**
   * Remove an image URL by the provided index.
   * At least 2 image URL's should remain to compare.
   */
  public remove(url: string): void {
    if (this.displayedImageUrls().length <= 2) {
      return;
    }

    this.displayedImageUrls.set(
      this.displayedImageUrls().filter((u) => u !== url),
    );
  }

  /**
   * Show all images again.
   *
   * Make sure to create a copy of the array containing all the image URL's,
   * because when removing an index only the {@see _displayedImageUrls} should
   * be altered.
   */
  public showAll(): void {
    this.displayedImageUrls.set(this.allImageUrls());
  }

  /**
   * Dismiss the modal containing this component.
   */
  public async dismiss(): Promise<void> {
    await this.modalController.dismiss();
  }
}
